import React, { useState, useEffect, useRef } from 'react';
import { Node } from '@xyflow/react';
import {
  X,
  Sliders,
  Sparkles,
  Bot,
  Brain,
  BookOpen,
  Globe,
  FolderGit2,
  Zap,
  LayoutTemplate,
  Mic,
  Database,
  Compass,
  Mail,
  CreditCard,
  PhoneForwarded,
  BellRing,
  ShoppingBag,
  Smartphone,
  KeyRound,
  Lock,
  ShieldCheck,
  ShieldAlert,
  Activity,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Wand2,
  ChevronRight,
  Check,
  Monitor,
  Camera,
  Eye,
  Phone,
  UploadCloud,
  Loader2
} from 'lucide-react';

interface NodeInspectorStudioProps {
  isOpen: boolean;
  onClose: () => void;
  node: Node | null;
  onUpdateNodeData: (nodeId: string, field: string, value: any, allUpdates?: any) => void;
  isWired?: boolean;
  onAutoWireNode?: (nodeId: string) => void;
  openPortalData?: (nodeType?: string, nodeId?: string) => void;
}

const getNodeIcon = (type?: string) => {
  switch (type) {
    case 'webWidgetNode':
    case 'WEB_WIDGET_NODE':
      return <Globe className="text-cyan-400" size={18} />;
    case 'lunaCoreNode':
    case 'LUNA_CORE_HUB':
      return <Sparkles className="text-cyan-400" size={18} />;
    case 'instructionNode':
      return <Bot className="text-sky-400" size={18} />;
    case 'lunaMemoryNode':
      return <Brain className="text-purple-400" size={18} />;
    case 'knowledgeNode':
      return <BookOpen className="text-emerald-400" size={18} />;
    case 'productCatalogNode':
      return <ShoppingBag className="text-emerald-400" size={18} />;
    case 'mediaVaultNode':
      return <FolderGit2 className="text-sky-400" size={18} />;
    case 'scraperNode':
      return <Globe className="text-indigo-400" size={18} />;
    case 'whatsappInstanceNode':
      return <Smartphone className="text-emerald-400" size={18} />;
    case 'callGuardNode':
      return <ShieldAlert className="text-rose-400" size={18} />;
    case 'securityFilterNode':
      return <ShieldCheck className="text-amber-400" size={18} />;
    case 'paymentGatewayNode':
      return <CreditCard className="text-emerald-400" size={18} />;
    case 'callTransferNode':
      return <PhoneForwarded className="text-indigo-400" size={18} />;
    case 'intentAlertNode':
      return <BellRing className="text-rose-400" size={18} />;
    case 'emailSmtpNode':
      return <Mail className="text-emerald-400" size={18} />;
    case 'formGeneratorNode':
      return <LayoutTemplate className="text-cyan-400" size={18} />;
    case 'voiceTypingNode':
      return <Mic className="text-purple-400" size={18} />;
    case 'databaseNode':
      return <Database className="text-teal-400" size={18} />;
    case 'authGuardNode':
      return <Lock className="text-rose-400" size={18} />;
    case 'authGatewayNode':
      return <KeyRound className="text-amber-400" size={18} />;
    case 'sectionNode':
      return <Compass className="text-indigo-400" size={18} />;
    default:
      return <Activity className="text-cyan-400" size={18} />;
  }
};

interface InspectorTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onDebouncedChange: (val: string) => void;
  debounceMs?: number;
}

const InspectorTextarea: React.FC<InspectorTextareaProps> = ({
  value,
  onDebouncedChange,
  debounceMs = 500,
  ...props
}) => {
  const [localVal, setLocalVal] = useState<string>(value || '');
  const isFocusedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const debounceTimerRef = useRef<any>(null);
  const cursorRef = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (!isFocusedRef.current && (value || '') !== localVal) {
      setLocalVal(value || '');
    }
  }, [value]);

  React.useLayoutEffect(() => {
    if (cursorRef.current && textareaRef.current && document.activeElement === textareaRef.current) {
      const { start, end } = cursorRef.current;
      textareaRef.current.setSelectionRange(start, end);
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    const start = e.target.selectionStart ?? next.length;
    const end = e.target.selectionEnd ?? next.length;
    cursorRef.current = { start, end };
    setLocalVal(next);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onDebouncedChange(next);
    }, debounceMs);
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    cursorRef.current = {
      start: e.currentTarget.selectionStart ?? e.currentTarget.value.length,
      end: e.currentTarget.selectionEnd ?? e.currentTarget.value.length,
    };
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    isFocusedRef.current = true;
    cursorRef.current = {
      start: e.currentTarget.selectionStart ?? e.currentTarget.value.length,
      end: e.currentTarget.selectionEnd ?? e.currentTarget.value.length,
    };
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    isFocusedRef.current = false;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    onDebouncedChange(localVal);
    props.onBlur?.(e);
  };

  return (
    <textarea
      {...props}
      ref={textareaRef}
      value={localVal}
      onChange={handleChange}
      onSelect={handleSelect}
      onKeyUp={handleSelect}
      onClick={handleSelect}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

interface InspectorInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: string;
  onDebouncedChange: (val: string) => void;
  debounceMs?: number;
}

const InspectorInput: React.FC<InspectorInputProps> = ({
  value,
  onDebouncedChange,
  debounceMs = 500,
  ...props
}) => {
  const [localVal, setLocalVal] = useState<string>(value || '');
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceTimerRef = useRef<any>(null);
  const cursorRef = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (!isFocusedRef.current && (value || '') !== localVal) {
      setLocalVal(value || '');
    }
  }, [value]);

  React.useLayoutEffect(() => {
    if (cursorRef.current && inputRef.current && document.activeElement === inputRef.current) {
      const { start, end } = cursorRef.current;
      inputRef.current.setSelectionRange(start, end);
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    const start = e.target.selectionStart ?? next.length;
    const end = e.target.selectionEnd ?? next.length;
    cursorRef.current = { start, end };
    setLocalVal(next);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onDebouncedChange(next);
    }, debounceMs);
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
    cursorRef.current = {
      start: e.currentTarget.selectionStart ?? e.currentTarget.value.length,
      end: e.currentTarget.selectionEnd ?? e.currentTarget.value.length,
    };
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true;
    cursorRef.current = {
      start: e.currentTarget.selectionStart ?? e.currentTarget.value.length,
      end: e.currentTarget.selectionEnd ?? e.currentTarget.value.length,
    };
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    onDebouncedChange(localVal);
    props.onBlur?.(e);
  };

  return (
    <input
      {...props}
      ref={inputRef}
      value={localVal}
      onChange={handleChange}
      onSelect={handleSelect}
      onKeyUp={handleSelect}
      onClick={handleSelect}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export const NodeInspectorStudio: React.FC<NodeInspectorStudioProps> = ({
  isOpen,
  onClose,
  node,
  onUpdateNodeData,
  isWired = true,
  onAutoWireNode,
  openPortalData,
}) => {
  const [activeTab, setActiveTab] = useState<'config' | 'ports'>('config');
  const [isExtractingForm, setIsExtractingForm] = useState(false);
  const [formExtractStatus, setFormExtractStatus] = useState<string | null>(null);
  const [formExtractError, setFormExtractError] = useState<string | null>(null);
  const formFileInputRef = useRef<HTMLInputElement | null>(null);

  if (!isOpen || !node) return null;

  const nodeData = (node.data || {}) as Record<string, any>;
  const nodeType = node.type || '';

  const handleFieldChange = (field: string, value: any, allUpdates?: any) => {
    onUpdateNodeData(node.id, field, value, allUpdates);
  };

  const handleFormDocExtract = async (file: File) => {
    if (!node || !file) return;
    setIsExtractingForm(true);
    setFormExtractStatus('Luna analyzing document & extracting questionnaire...');
    setFormExtractError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (nodeData.documentTitle) {
        formData.append('documentTitle', nodeData.documentTitle);
      }
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null;
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/admin/luna-flows/extract-form-from-doc', {
        method: 'POST',
        headers,
        body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to extract form schema');
      const ext = json.extractedForm;
      handleFieldChange('documentTitle', ext.title || file.name.replace(/\.[^/.]+$/, ''), {
        schemaType: ext.schemaType,
        sections: ext.sections,
        autoGenerateSchema: true,
        documentContent: ext.description
      });
      setFormExtractStatus(`✨ Extracted ${ext.sections.length} sections & ${ext.totalFields} questions!`);
      setTimeout(() => setFormExtractStatus(null), 5000);
    } catch (err: any) {
      setFormExtractError(err?.message || 'Extraction failed');
      setTimeout(() => setFormExtractError(null), 6000);
    } finally {
      setIsExtractingForm(false);
      if (formFileInputRef.current) formFileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-[460px] bg-slate-950/95 backdrop-blur-2xl border-l border-cyan-500/30 shadow-[0_0_80px_rgba(0,0,0,0.85)] flex flex-col transition-transform duration-300 animate-in slide-in-from-right">
      {/* Drawer Header */}
      <div className="p-4 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-slate-800/90 border border-slate-700 shadow-inner">
            {getNodeIcon(nodeType)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-white tracking-wide">
                {nodeData.agentName || nodeData.personaTitle || nodeData.instanceLabel || nodeData.documentTitle || nodeType}
              </h3>
              <span className="text-[9px] font-mono uppercase font-bold px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                {nodeType.replace('Node', '')}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {node.id}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {openPortalData && (
            <button
              type="button"
              onClick={() => openPortalData(nodeType, node.id)}
              className="p-1.5 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 transition-colors"
              title="Open Portal Data Studio"
            >
              <ExternalLink size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-colors cursor-pointer"
            title="Collapse Inspector"
          >
            <ChevronRight size={14} />
            <span className="hidden sm:inline">Collapse</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer"
            title="Close Inspector"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Wire & Health Diagnostic Status Pill */}
      <div className="px-4 py-2 bg-slate-900/40 border-b border-slate-800/80 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {isWired ? (
            <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-400" />
              Connected to Luna Core
            </span>
          ) : (
            <span className="text-[11px] font-bold text-amber-400 flex items-center gap-1.5 animate-pulse">
              <AlertTriangle size={13} className="text-amber-400" />
              Unplugged from Core Hub
            </span>
          )}
        </div>

        {!isWired && onAutoWireNode && (
          <button
            type="button"
            onClick={() => onAutoWireNode(node.id)}
            className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 flex items-center gap-1 shadow-[0_0_10px_rgba(6,182,212,0.4)] cursor-pointer"
          >
            <Wand2 size={11} /> Auto-Wire
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 bg-slate-900/30 shrink-0">
        <button
          type="button"
          onClick={() => setActiveTab('config')}
          className={`flex-1 py-2 text-xs font-bold transition-all border-b-2 ${
            activeTab === 'config'
              ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          ⚙️ Parameters & Logic
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('ports')}
          className={`flex-1 py-2 text-xs font-bold transition-all border-b-2 ${
            activeTab === 'ports'
              ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          🔌 Port Wiring
        </button>
      </div>

      {/* Drawer Scrollable Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'config' ? (
          <div className="space-y-4">
            {/* Dynamic fields by Node Type */}
            {/* 1. LunaCoreNode */}
            {(nodeType === 'lunaCoreNode' || nodeType === 'LUNA_CORE_HUB') && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Agent Name</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.agentName || ''}
                    onDebouncedChange={(val) => handleFieldChange('agentName', val)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Base Voice</label>
                  <select
                    value={nodeData.baseVoice || 'Kore'}
                    onChange={(e) => handleFieldChange('baseVoice', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="Kore">Kore (Firm & Balanced - Female)</option>
                    <option value="Aoede">Aoede (Warm & Empathetic - Female)</option>
                    <option value="Puck">Puck (Energetic & Playful - Male)</option>
                    <option value="Fenrir">Fenrir (Deep & Authoritative - Male)</option>
                    <option value="Charon">Charon (Calm & Professional - Male)</option>
                    <option value="Zephyr">Zephyr (Light & Friendly - Female)</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Language Priority</label>
                  <select
                    value={nodeData.languageMode || 'bilingual'}
                    onChange={(e) => handleFieldChange('languageMode', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="bilingual">Bilingual (Sinhala & English Priority)</option>
                    <option value="english">English Only</option>
                    <option value="sinhala">Sinhala Only (සිංහල)</option>
                    <option value="multilingual">Multilingual Global Auto-Detect</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <div>
                    <span className="text-xs font-bold text-white block">Natural Spoken Sinhala</span>
                    <span className="text-[10px] text-slate-400">Optimizes Luna Neural Voice for colloquial Sri Lankan cadence</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={nodeData.naturalSpokenSinhala !== false}
                    onChange={(e) => handleFieldChange('naturalSpokenSinhala', e.target.checked)}
                    className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex justify-between text-[11px] font-bold text-slate-300 mb-1">
                    <span>Creativity & Temperature</span>
                    <span className="text-cyan-400 font-mono">{nodeData.temperature ?? 0.7}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={nodeData.temperature ?? 0.7}
                    onChange={(e) => handleFieldChange('temperature', parseFloat(e.target.value))}
                    className="w-full accent-cyan-400 cursor-pointer"
                  />
                </div>
              </>
            )}

            {/* 2. InstructionNode */}
            {nodeType === 'instructionNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Persona Role Title</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.personaTitle || ''}
                    onDebouncedChange={(val) => handleFieldChange('personaTitle', val)}
                    placeholder="e.g. Retail Sales Concierge"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">System Instructions & Directives</label>
                  <InspectorTextarea
                    rows={6}
                    value={nodeData.instructions || ''}
                    onDebouncedChange={(val) => handleFieldChange('instructions', val)}
                    placeholder="Describe how Luna should speak, behave, answer questions, and handle customer requests..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono leading-relaxed"
                  />
                </div>
              </>
            )}

            {/* 3. WhatsAppInstanceNode */}
            {nodeType === 'whatsappInstanceNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Channel Label</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.instanceLabel || ''}
                    onDebouncedChange={(val) => handleFieldChange('instanceLabel', val)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Registered Phone Number</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.phoneNumber || ''}
                    onDebouncedChange={(val) => handleFieldChange('phoneNumber', val)}
                    placeholder="+94771234567"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <div>
                    <span className="text-xs font-bold text-white block">Live 2-Way Voice (SIP)</span>
                    <span className="text-[10px] text-slate-400">Enables answering live WhatsApp voice calls via Luna Live Voice</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={nodeData.enableSipCalling !== false}
                    onChange={(e) => handleFieldChange('enableSipCalling', e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Anti-Ban Typing Speed</label>
                  <select
                    value={nodeData.antiBanTypingSpeed || 'natural'}
                    onChange={(e) => handleFieldChange('antiBanTypingSpeed', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  >
                    <option value="natural">Natural Human Simulation</option>
                    <option value="fast">Fast Automated Response</option>
                    <option value="cautious">Cautious (High Delay Protection)</option>
                  </select>
                </div>
              </>
            )}

            {/* 4. CallGuardNode */}
            {nodeType === 'callGuardNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Max Call Duration (Minutes)</label>
                  <input
                    type="number"
                    value={nodeData.maxCallDurationMinutes ?? 5}
                    onChange={(e) => handleFieldChange('maxCallDurationMinutes', parseInt(e.target.value) || 5)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Silence Auto-Hangup (Seconds)</label>
                  <input
                    type="number"
                    value={nodeData.silenceSeconds ?? 15}
                    onChange={(e) => handleFieldChange('silenceSeconds', parseInt(e.target.value) || 15)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <div>
                    <span className="text-xs font-bold text-white block">Anti-Token Burn</span>
                    <span className="text-[10px] text-slate-400">Terminates dead connections automatically</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={nodeData.antiTokenBurn !== false}
                    onChange={(e) => handleFieldChange('antiTokenBurn', e.target.checked)}
                    className="w-4 h-4 rounded accent-rose-500 cursor-pointer"
                  />
                </div>
              </>
            )}

            {/* 5. PaymentGatewayNode */}
            {nodeType === 'paymentGatewayNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={nodeData.bankName || ''}
                    onChange={(e) => handleFieldChange('bankName', e.target.value)}
                    placeholder="e.g. Commercial Bank of Ceylon"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Account Number</label>
                  <input
                    type="text"
                    value={nodeData.accountNumber || ''}
                    onChange={(e) => handleFieldChange('accountNumber', e.target.value)}
                    placeholder="e.g. 8001234567"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Account Holder Name</label>
                  <input
                    type="text"
                    value={nodeData.accountName || ''}
                    onChange={(e) => handleFieldChange('accountName', e.target.value)}
                    placeholder="e.g. Kangavena Enterprise (Pvt) Ltd"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Online Payment Link URL (Optional)</label>
                  <input
                    type="text"
                    value={nodeData.paymentLinkUrl || ''}
                    onChange={(e) => handleFieldChange('paymentLinkUrl', e.target.value)}
                    placeholder="https://payhere.lk/pay/..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>
              </>
            )}

            {/* 6. KnowledgeNode */}
            {nodeType === 'knowledgeNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">RAG Context & FAQ Rules</label>
                  <InspectorTextarea
                    rows={8}
                    value={nodeData.contextSource || ''}
                    onDebouncedChange={(val) => handleFieldChange('contextSource', val)}
                    placeholder="Paste business policies, FAQs, price tiers, and service specifications..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono text-[11px] leading-relaxed"
                  />
                </div>
              </>
            )}

            {/* 7. CallTransferNode */}
            {nodeType === 'callTransferNode' && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Transfer Destination Phone</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.transferPhoneNumber || ''}
                    onDebouncedChange={(val) => handleFieldChange('transferPhoneNumber', val)}
                    placeholder="+94771234567"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Escalation Agent Name</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.agentName || ''}
                    onDebouncedChange={(val) => handleFieldChange('agentName', val)}
                    placeholder="Senior Duty Manager"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
              </>
            )}

            {/* 8. WebWidgetNode */}
            {(nodeType === 'webWidgetNode' || nodeType === 'WEB_WIDGET_NODE') && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Authorized Web Domains</label>
                  <input
                    type="text"
                    value={nodeData.allowedDomains || ''}
                    onChange={(e) => handleFieldChange('allowedDomains', e.target.value)}
                    placeholder="e.g. successmind.lk, *.successmind.lk"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                  <div>
                    <span className="text-xs font-bold text-white block">Real-Time Screen Vision</span>
                    <span className="text-[10px] text-slate-400">Luna Vision multimodal screen streaming for PDFs, notes & visual co-pilot</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={nodeData.enableScreenVision !== false}
                    onChange={(e) => handleFieldChange('enableScreenVision', e.target.checked)}
                    className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                  />
                </div>

                {nodeData.enableScreenVision !== false && (
                  <div className="p-3 rounded-xl bg-slate-900/80 border border-cyan-500/20 space-y-3 animate-fade-in text-xs">
                    <div>
                      <label className="text-[11px] font-bold text-slate-300 block mb-1">Vision Trigger Mode</label>
                      <select
                        value={nodeData.screenCaptureTriggerMode || 'auto_on_demand'}
                        onChange={(e) => handleFieldChange('screenCaptureTriggerMode', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                      >
                        <option value="auto_on_demand">Auto On-Demand (Smart detection on PDF/Questions)</option>
                        <option value="always_on_start">Always on Start (Auto-prompt upon connection)</option>
                        <option value="manual_icon_only">Manual Icon Only (Discreet 👁️ widget button)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] font-bold text-slate-300 block mb-1">Capture Frequency</label>
                        <select
                          value={nodeData.screenCaptureFps || '1_fps'}
                          onChange={(e) => handleFieldChange('screenCaptureFps', e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                        >
                          <option value="1_fps">1 FPS (Balanced)</option>
                          <option value="0.5_fps">0.5 FPS (Eco Mode)</option>
                          <option value="2_fps">2 FPS (Smooth)</option>
                          <option value="on_speech">On-Speech (Zero Idle)</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-slate-300 block mb-1">Resolution Quality</label>
                        <select
                          value={nodeData.screenCaptureQuality || '720p'}
                          onChange={(e) => handleFieldChange('screenCaptureQuality', e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                        >
                          <option value="720p">720p HD (Optimal)</option>
                          <option value="1080p">1080p Full HD (Crisp)</option>
                          <option value="480p">480p (Low Bandwidth)</option>
                        </select>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-800 space-y-2 text-[11px]">
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-slate-300">Auto-Detect PDFs & Lesson Materials</span>
                        <input
                          type="checkbox"
                          checked={nodeData.pdfAutoDetectEnabled !== false}
                          onChange={(e) => handleFieldChange('pdfAutoDetectEnabled', e.target.checked)}
                          className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                        />
                      </label>
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-slate-300">Show Zero-Data Privacy Shield Badge</span>
                        <input
                          type="checkbox"
                          checked={nodeData.privacyBadgeEnabled !== false}
                          onChange={(e) => handleFieldChange('privacyBadgeEnabled', e.target.checked)}
                          className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                        />
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Web Persona Directives</label>
                  <InspectorTextarea
                    rows={3}
                    value={nodeData.webPersonaDirectives || ''}
                    onDebouncedChange={(val) => handleFieldChange('webPersonaDirectives', val)}
                    placeholder="Custom directives specifically for website visitors..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono leading-relaxed"
                  />
                </div>
              </>
            )}

            {/* 9. FormGeneratorNode (Multi-Step Questionnaire & Form Engine) */}
            {(nodeType === 'formGeneratorNode' || nodeType === 'FORM_GENERATOR' || nodeType === 'form_generator') && (
              <>
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Form / Questionnaire Title</label>
                  <InspectorInput
                    type="text"
                    value={nodeData.documentTitle || ''}
                    onDebouncedChange={(val) => handleFieldChange('documentTitle', val)}
                    placeholder="e.g. Income Tax Declaration 2026 / Client KYC Intake"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-bold"
                  />
                </div>

                {/* Multimodal AI Form Auto-Extraction */}
                <div className="space-y-1.5">
                  <input
                    type="file"
                    ref={formFileInputRef}
                    disabled={isExtractingForm}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFormDocExtract(f);
                    }}
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                    className="hidden"
                  />
                  <button
                    type="button"
                    disabled={isExtractingForm}
                    onClick={() => formFileInputRef.current?.click()}
                    className="w-full py-2 px-3 rounded-xl bg-gradient-to-r from-purple-900/70 via-indigo-900/70 to-cyan-900/70 hover:from-purple-800/90 hover:via-indigo-800/90 hover:to-cyan-800/90 border border-purple-500/40 hover:border-cyan-400 text-slate-100 text-xs font-bold flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(168,85,247,0.3)] transition cursor-pointer active:scale-[0.98] disabled:opacity-50"
                  >
                    {isExtractingForm ? (
                      <>
                        <Loader2 size={13} className="animate-spin text-cyan-300" />
                        <span className="text-cyan-200">Luna AI Analyzing Document...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={13} className="text-purple-300 animate-pulse" />
                        <span>Build Form from Doc / Image</span>
                        <UploadCloud size={13} className="text-cyan-300" />
                      </>
                    )}
                  </button>

                  {formExtractStatus && (
                    <div className="p-2 rounded-xl bg-cyan-950/70 border border-cyan-400/40 text-cyan-300 text-[11px] font-mono flex items-center gap-2">
                      <Sparkles size={12} className="text-cyan-400" />
                      <span>{formExtractStatus}</span>
                    </div>
                  )}
                  {formExtractError && (
                    <div className="p-2 rounded-xl bg-rose-950/70 border border-rose-500/40 text-rose-300 text-[11px] font-mono flex items-center gap-2">
                      <AlertTriangle size={12} className="text-rose-400" />
                      <span>{formExtractError}</span>
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-xl bg-emerald-950/20 border border-emerald-500/30 space-y-2">
                  <label className="text-[11px] font-bold text-emerald-300 block flex items-center gap-1.5">
                    <Phone size={13} className="text-emerald-400" />
                    Store Owner / Manager WhatsApp Destination
                  </label>
                  <InspectorInput
                    type="text"
                    value={nodeData.ownerAlertPhone || ''}
                    onDebouncedChange={(val) => handleFieldChange('ownerAlertPhone', val)}
                    placeholder="+94771234567 (Blank = Default Store Phone)"
                    className="w-full bg-slate-900 border border-emerald-500/30 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-400 font-mono"
                  />
                  <p className="text-[10px] text-emerald-400/80 font-mono">
                    Completed forms are automatically summarized and dispatched to this WhatsApp number upon customer confirmation.
                  </p>
                </div>

                <div className="space-y-2 pt-1 border-t border-slate-800">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div>
                      <span className="text-xs font-bold text-white block">2-Step Confirmation</span>
                      <span className="text-[10px] text-slate-400">Require customer to verify collected data before sending</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={nodeData.requireCustomerConfirmation !== false}
                      onChange={(e) => handleFieldChange('requireCustomerConfirmation', e.target.checked)}
                      className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div>
                      <span className="text-xs font-bold text-white block">Missed Question Recovery</span>
                      <span className="text-[10px] text-slate-400">Re-prompt customer if a mandatory question is skipped</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={nodeData.enableMissedFieldRecovery !== false}
                      onChange={(e) => handleFieldChange('enableMissedFieldRecovery', e.target.checked)}
                      className="w-4 h-4 rounded accent-cyan-500 cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">Document Rules & Assessment Guidelines</label>
                  <InspectorTextarea
                    rows={5}
                    value={nodeData.documentContent || ''}
                    onDebouncedChange={(val) => handleFieldChange('documentContent', val)}
                    placeholder="Paste statutory rules, income brackets, relief formulas, or instructions..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono text-[11px] leading-relaxed"
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          /* Port Wiring Telemetry */
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-slate-900/70 border border-slate-800">
              <h4 className="text-xs font-bold text-cyan-300 mb-1">Star-Topology Routing</h4>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Auxiliary capability nodes register declarative tools and RAG context into Luna Core Hub. Response telemetry streams dynamically to connected output channels.
              </p>
            </div>

            <div className="space-y-2">
              <div className="p-3 rounded-xl bg-slate-900/40 border border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-400">Node Identifier:</span>
                <span className="text-white font-mono font-bold">{node.id}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/40 border border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-400">Canonical Port Type:</span>
                <span className="text-cyan-300 font-mono font-bold">{nodeType}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-900/40 border border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-400">Wiring Status:</span>
                <span className={`font-mono font-bold ${isWired ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {isWired ? 'ACTIVE (WIRED)' : 'DISCONNECTED'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] border-t border-slate-800 bg-slate-900/90 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
          <CheckCircle2 size={13} className="text-emerald-400 animate-pulse shrink-0" />
          <span className="hidden xs:inline">Auto-persisting live to graph</span>
          <span className="xs:hidden">Auto-saved</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-4 sm:px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all cursor-pointer active:scale-95 flex items-center gap-1.5 font-sans"
        >
          <Check size={14} className="stroke-[3]" />
          <span>Save & Close</span>
        </button>
      </div>
    </div>
  );
};

export default NodeInspectorStudio;
